import dayjs from 'dayjs'

import {
  ConfirmDeleteAlertModal,
  CoreAlertsModal,
  CreateAlertModal,
  ManageAlertsModal,
} from '@/feature/alerts/components/'
import { useGetUser } from '@/feature/auth'
import { CreateTaskModal } from '@/feature/execution-board'
import { FeatureFlag, WhenFlag } from '@/feature/feature-flags'
import { useTagsStore } from '@/feature/tags/store'
import { useContactModalStore } from '@/lib/stores/contactModalStore'
import { useCreateTaskModalStore } from '@/feature/execution-board/components/CreateTaskModal/createTaskModalStore'
import { useShareAssetModalStore } from '@/lib/stores/shareAssetModalStore'
import { FormattedMessage } from 'react-intl'

import { CommandPalette } from '@/lib/components/CommandPalette'
import { BulkTagsModal } from '@/lib/features/Assets/components/BulkTagsModal'

import { DATE_FORMAT } from '@/lib/core/constants/constants'
import { useCurrency } from '@/lib/core/hooks/useCurrency'

import { AddContactModal } from '@/lib/features/Contacts/components/AddContactModal'
import { ShareAssetModal, SharesSummaryModal } from '@/lib/features/SmartShares/components'
import {
  getCost,
  OrganizationName,
  SharedWith,
} from '@/lib/features/SmartShares/components/ShareAssetModal'
import shareAssetMessages from '@/lib/features/SmartShares/components/ShareAssetModal/ShareAssetModal.messages'

//This component is used to display the summary of assets that were shared via Smart Sharing
const GlobalSharesSummaryModal = () => {
  const {
    addedShare: storeAddedShare,
    showSharesSummaryModal: storeShowSharesSummaryModal,
    onSuccessCallback: storeOnSuccessCallback,
    setAddedShare: setStoreAddedShare,
    setShowSharesSummaryModal: setStoreShowSharesSummaryModal,
  } = useShareAssetModalStore()

  const { formatAmount } = useCurrency()
  const { data: user } = useGetUser()

  if (!storeShowSharesSummaryModal || !storeAddedShare) {
    return null
  }

  const onCloseHandler = () => {
    setStoreAddedShare(null)
    setStoreShowSharesSummaryModal(false)
    // Call the success callback to refresh data on the page that initiated the share
    storeOnSuccessCallback?.()
    useShareAssetModalStore.getState().closeModal()
    useContactModalStore.getState().closeModal()
  }

  return (
    <SharesSummaryModal
      isActive={storeShowSharesSummaryModal}
      title={<FormattedMessage {...shareAssetMessages.summaryModalTitle} />}
      subtitle={
        <SharedWith>
          <FormattedMessage {...shareAssetMessages.sharedWith} tagName="span" />{' '}
          <OrganizationName>{storeAddedShare[0]?.organizationTo.name}</OrganizationName>
        </SharedWith>
      }
      shares={storeAddedShare.map(({ asset, dateFrom, dateTo, reference }) => {
        const dates = `${dayjs(dateFrom).format(DATE_FORMAT)} - ${dayjs(dateTo).format(DATE_FORMAT)}`
        const days = dayjs(dateTo).diff(dateFrom, 'days') + 1
        const cost = formatAmount(
          getCost(
            user?.organization?.dailyRentCost ? parseFloat(user.organization.dailyRentCost) : 0,
            dateFrom,
            dateTo,
          ),
        )

        return {
          assetId: asset.bridgeId,
          dates,
          days,
          cost,
          reference,
          description: asset.description,
        }
      })}
      onClose={onCloseHandler}
    />
  )
}

// Global AddContactModal component
const GlobalAddContactModal = () => {
  const { isOpen: addContactModalOpen, closeModal: closeContactModal } = useContactModalStore()

  return (
    <AddContactModal
      isActive={addContactModalOpen}
      onClose={(withSuccess) => {
        const { onSuccessCallback } = useContactModalStore.getState()
        closeContactModal()

        if (withSuccess) {
          onSuccessCallback?.()
        }
      }}
    />
  )
}

const GlobalCreateTaskModal = () => {
  const { isOpen, initialData, closeModal } = useCreateTaskModalStore()

  if (!isOpen) return null

  return <CreateTaskModal isActive={isOpen} onClose={closeModal} initialData={initialData} />
}

const GlobalBulkTagsModal = () => {
  const { isBulkTagsModalOpen, bulkTagsSelectedAssets, closeBulkTagsModal } = useTagsStore()

  return (
    <BulkTagsModal
      isOpen={isBulkTagsModalOpen}
      selectedAssets={bulkTagsSelectedAssets}
      onClose={closeBulkTagsModal}
      onApplySuccess={() => {
        useTagsStore.getState().bulkTagsOnSuccess?.()
        closeBulkTagsModal()
      }}
    />
  )
}

/**
 * GlobalModals component that renders all global modals used throughout the app.
 * This centralizes modal management and keeps _app.tsx clean.
 */
export const GlobalModals = () => {
  return (
    <>
      <CommandPalette />
      <ShareAssetModal isActive={false} />
      <GlobalSharesSummaryModal />
      <GlobalAddContactModal />
      <FeatureFlag flag="featureExecutionBoardUi">
        <WhenFlag>
          <ManageAlertsModal />
          <CreateAlertModal />
          <CoreAlertsModal />
          <ConfirmDeleteAlertModal />
          <GlobalCreateTaskModal />
        </WhenFlag>
      </FeatureFlag>
      <FeatureFlag flag="assetTags">
        <WhenFlag>
          <GlobalBulkTagsModal />
        </WhenFlag>
      </FeatureFlag>
    </>
  )
}
